import { useCallback, useEffect, useRef, useState } from "react";

import { isApiError } from "../api/errors.js";

export interface AsyncResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Re-runs the loader. */
  reload: () => void;
}

/**
 * Runs an async loader on mount (and when `deps` change), tracking loading and
 * error state. API errors surface their human-readable message.
 */
export function useAsync<T>(
  loader: () => Promise<T>,
  deps: ReadonlyArray<unknown>
): AsyncResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loader()
      .then((result) => {
        if (!cancelled && mounted.current) {
          setData(result);
        }
      })
      .catch((err: unknown) => {
        if (cancelled || !mounted.current) {
          return;
        }
        setError(isApiError(err) ? err.message : "Something went wrong.");
      })
      .finally(() => {
        if (!cancelled && mounted.current) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, reload };
}
